import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, CheckCircle, XCircle, Clock, Send, ExternalLink } from 'lucide-react';
import { emailService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import type { SentEmail, EmailCredential } from '../../types/email';
import { formatDateTime } from '../../lib/formatters';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  EmptyState,
  Pagination,
  FormSelect,
  Modal,
} from '../ui';

interface SentEmailsListProps {
  credentials?: EmailCredential[];
  showCredentialFilter?: boolean;
}

function SentEmailsList({ credentials = [], showCredentialFilter = false }: SentEmailsListProps) {
  const [page, setPage] = useState(1);
  const [type, setType] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<SentEmail | null>(null);
  const [selectedCredential, setSelectedCredential] = useState<string>('');
  const queryClient = useQueryClient();

  const { data: response, isLoading } = useQuery({
    queryKey: ['sent-emails', type, page, selectedCredential],
    queryFn: () =>
      emailService.getSentEmails(
        type,
        page,
        selectedCredential ? Number(selectedCredential) : undefined
      ),
  });

  // Get email details when selected
  const { data: emailDetails } = useQuery({
    queryKey: ['sent-email-details', selectedEmail?.id],
    queryFn: () => emailService.getSentEmailDetails(selectedEmail!.id),
    enabled: !!selectedEmail,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailService.deleteSentEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sent-emails'] });
      messageHandler.success('Email excluído com sucesso');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleDelete = (email: SentEmail) => {
    messageHandler.confirm('Excluir Email', 'Tem certeza que deseja excluir este email?', () =>
      deleteMutation.mutate(email.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <Badge variant="success" icon={CheckCircle}>
            Enviado
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="error" icon={XCircle}>
            Falhou
          </Badge>
        );
      default:
        return (
          <Badge variant="warning" icon={Clock}>
            Pendente
          </Badge>
        );
    }
  };

  const truncateText = (text: string, maxLength: number = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const emails = response?.data || [];
  const pagination = response?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-4">
        {showCredentialFilter && (
          <div className="w-64">
            <FormSelect
              value={selectedCredential}
              onChange={e => {
                setSelectedCredential(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas as contas</option>
              {credentials.map(credential => (
                <option key={credential.id} value={credential.id}>
                  {credential.name}
                </option>
              ))}
            </FormSelect>
          </div>
        )}
        <div className="w-48">
          <FormSelect
            value={type}
            onChange={e => {
              setType(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="sent">Enviados</option>
            <option value="replied">Respostas</option>
          </FormSelect>
        </div>
      </div>

      {emails.length > 0 ? (
        <div className="overflow-x-auto">
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-48 lg:w-64">Para</TableHead>
                  <TableHead>Assunto</TableHead>
                  <TableHead className="w-32 hidden md:table-cell">Status</TableHead>
                  <TableHead className="w-40 hidden lg:table-cell">Data</TableHead>
                  <TableHead className="w-24 hidden sm:table-cell">Anexos</TableHead>
                  <TableHead className="w-32">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map(email => (
                  <TableRow key={email.id}>
                    <TableCell className="truncate max-w-[200px] lg:max-w-[300px]">
                      {email.to_email}
                    </TableCell>
                    <TableCell>
                      <div className="truncate max-w-[300px] lg:max-w-[400px]">{email.subject}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {getStatusBadge(email.status)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {email.sent_at ? formatDateTime(email.sent_at) : '-'}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {email.attachments?.length > 0 && (
                        <Badge variant="secondary">{email.attachments.length}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedEmail(email)}
                          title="Ver detalhes"
                        >
                          <ExternalLink className="h-4 w-4 text-primary-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(email)}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <EmptyState
          icon={<Send className="h-12 w-12" />}
          title="Nenhum email encontrado"
          description="Não há emails enviados para exibir."
        />
      )}

      {pagination && pagination.last_page > 1 && (
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.last_page}
          onPageChange={setPage}
        />
      )}

      {/* Email Details Modal */}
      <Modal
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        title="Detalhes do Email"
        size="xl"
      >
        {emailDetails && (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500">Para:</p>
                  <p className="font-medium">{emailDetails.email.to_email}</p>
                </div>
                <div className="text-sm text-gray-500">
                  {emailDetails.email.sent_at ? formatDateTime(emailDetails.email.sent_at) : '-'}
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">De:</p>
                <p className="font-medium">{emailDetails.email.from_email}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Assunto:</p>
                <p className="font-medium">{emailDetails.email.subject}</p>
              </div>

              {emailDetails.email.original_subject && (
                <div>
                  <p className="text-sm text-gray-500">Assunto Original:</p>
                  <p className="font-medium">{emailDetails.email.original_subject}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">Status:</p>
                {getStatusBadge(emailDetails.email.status)}
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-2">Conteúdo:</p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg whitespace-pre-wrap">
                {emailDetails.email.body}
              </div>
            </div>

            {emailDetails.email.attachments?.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">Anexos:</h3>
                <div className="space-y-2">
                  {emailDetails.email.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                    >
                      <span className="text-sm">{attachment.filename}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(attachment.file_path, '_blank')}
                      >
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {emailDetails.originalEmail && (
              <div>
                <h3 className="font-medium mb-2">Email Original:</h3>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">De:</p>
                    <p>{emailDetails.originalEmail.sender_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Assunto:</p>
                    <p>{emailDetails.originalEmail.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Recebido em:</p>
                    <p>{formatDateTime(emailDetails.originalEmail.received_at)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button variant="secondary" onClick={() => setSelectedEmail(null)}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SentEmailsList;
